import { createSelector } from '@reduxjs/toolkit';
import { PillType } from 'features/popularSearches';
import { selectUrlSubdirectory, routingSelectors, selectMapCoordinatesQueryString } from 'features/routing';
import {
  LocationType,
  selectLocationType,
  selectSearchCount,
  activeAdultCommunitiesSelector,
  selectConsumerPromos,
  selectCondosAndTownHomes,
  selectMarketingStatus,
  selectHomeStatus,
  hasInventoryStatusCombination,
  hasReadyToBuild,
  selectFetchResultListingsStatus,
  selectPage,
  selectPageSize,
  selectListingCount,
  selectBedrooms,
  selectBathrooms,
  selectGatedCommunity,
  selectGolfCourse,
  selectGreen,
  selectLuxury,
  selectMaximumPrice,
  selectMinimumPrice,
  selectNatureAreas,
  selectParks,
  selectPool,
  selectSorting,
  selectSportsFacilities,
  selectViews,
  selectWaterfront,
  SortType,
  selectResultType,
  selectProductType,
  selectLivingAreas,
  selectGarages,
  selectStories,
  selectPrimaryBedroom,
  selectRvGarage,
  selectReviews,
  selectMoveInDateRange,
  hasAvailableNow,
  selectPriceReduction,
  ResultType,
  selectPaging,
  selectLocation,
  selectAppliedFacets,
  selectSearchParameters,
  selectHomeOwnersAssociationFee,
  selectHomesCount,
  selectFocusedSearchBoundsForUrl,
  selectFocusedSearchActive,
} from 'features/searchResultsFacets';
import { selectDisplaySkeleton, selectResultsPresentationType } from 'layout/redux/selectors';
import { isComingSoon, isCloseOut, isGrandOpening, isActive } from 'services/marketingStatusValidator';
import getProductResultTypeToRedirect from '../services/autoSelectTargetService';

const { urlComponentsSelector } = routingSelectors;

/**
 * Selector  that maps different pill types to their corresponding filter values.
 */
export const selectPopularPillFilters = createSelector(
  selectConsumerPromos,
  selectCondosAndTownHomes,
  activeAdultCommunitiesSelector,
  selectMarketingStatus,
  selectHomeStatus,
  (consumerPromos, condosAndTownHomes, activeAdultCommunities, marketingStatus, homeStatus) => ({
    [PillType.HotDeals.toString()]: consumerPromos,
    [PillType.CondosAndTownhomes.toString()]: condosAndTownHomes,
    [PillType.Adult.toString()]: activeAdultCommunities.only,
    [PillType.ComingSoon.toString()]: isComingSoon(marketingStatus),
    [PillType.FloorPlans.toString()]: hasReadyToBuild(homeStatus),
    [PillType.QuickMoveIns.toString()]: hasInventoryStatusCombination(homeStatus),
  })
);

/**
 * Composed selector that takes three input selectors the counts, urlSubdirectory and locationType
 * to paint the paints with this parameters information. The popularPillFilters is useful to check or un check pill.
 */
export const selectPopularSearchesPills = createSelector(
  selectSearchCount,
  selectUrlSubdirectory,
  selectLocationType,
  selectPopularPillFilters,
  (searchCounts, urlSubdirectory, locationType, popularPillFilters) => {
    const pillsCounts = {
      [PillType.HotDeals.toString()]: searchCounts.hotDealsCount,
      [PillType.CondosAndTownhomes.toString()]: searchCounts.multiFamilyCount, 
      [PillType.Adult.toString()]: searchCounts.adultCount,
      [PillType.QuickMoveIns.toString()]: searchCounts.quickMoveInCount,
      [PillType.FloorPlans.toString()]: searchCounts.buildablePlansCount,
      [PillType.ComingSoon.toString()]: searchCounts.comingSoonCount,
    };

    const pillsWithCounts = Object.keys(pillsCounts).filter((pillType) => pillsCounts[pillType] > 0);

    return {
      pillsWithCounts,
      urlSubdirectory,
      locationType,
      popularPillFilters,
    };
  }
);

/**
 * Composed selector to map the subdirectory and location type. */
export const selectUrlSubdirectoryAndLocationType = createSelector(
  selectUrlSubdirectory,
  selectLocationType,
  (urlSubdirectory, locationType) => ({
    urlSubdirectory,
    locationType,
  })
);

export const urlComponentsWithHomeStatusSelector = createSelector(
  urlComponentsSelector,
  selectHomeStatus,
  (urlComponents, homeStatus) => ({
    urlComponents,
    homeStatus,
  })
);


/**
 * Selector composed by the results to be loaded, current results presentation type and fetch result listings status.
 */
export const selectResultsLoadedAndResultsPresentationType = createSelector(
  selectResultsPresentationType,
  selectFetchResultListingsStatus,
  (presentationType, fetchListingStatus) => ({
    presentationType,
    fetchListingStatus,
  })
);

/**
 * Selector composed by the results presentation type and the fetch result listings status.
 */
export const resultStatusAndResultsPresentationType = createSelector(
  selectResultsPresentationType,
  selectFetchResultListingsStatus,
  (presentationType, fetchResultListingStatus) => ({
    presentationType,
    fetchResultListingStatus,
  })
);

/**
 * Selector to get a flag indicating if the grid skeleton needs to be shown,
 * and also the current presentation type of the Search Results Page
 */
export const selectDisplaySkeletonAndResultsPresentationType = createSelector(
  selectDisplaySkeleton,
  selectResultsPresentationType,
  (shouldDisplaySkeleton, resultsPresentationType) => ({
    shouldDisplaySkeleton,
    resultsPresentationType,
  })
);

/**
 * Selector composed by the current presentation type and the page number served to the user.
 */
export const selectPagingAndResultPresentationType = createSelector(
  selectResultsPresentationType,
  selectPage,
  (resultsPresentationType, page) => ({
    resultsPresentationType,
    page,
  })
);

/**
 * This selector is composed by the fetch listings status, current page number, amount of results shown per page
 * and the total of results of the current search.
 * With this set of information, we know if we already saw the total of results the current Search Results Page
 * has to offer, or is there are still more results to fetch for the user.
 */
export const selectPageInformationAndFetchResultsStatus = createSelector(
  selectFetchResultListingsStatus,
  selectResultsPresentationType,
  selectPage,
  selectPageSize,
  selectListingCount,
  (fetchResultListingsStatus, resultPresentationType, page, pageSize, listingCount) => ({
    fetchResultListingsStatus,
    resultPresentationType,
    page,
    pageSize,
    listingCount,
  })
);

/**
 * This selector is meant for derive the Search Results Page query string based in the current store values.
 * The selector transforms the store data into a string that represents the filters applied by the user.
 */
export const selectConstructQueryString = createSelector(
  selectBedrooms,
  selectBathrooms,
  selectNatureAreas,
  selectGreen,
  selectPool,
  selectGatedCommunity,
  selectGolfCourse,
  selectSportsFacilities,
  selectParks,
  selectViews,
  selectWaterfront,
  selectLuxury,
  selectMaximumPrice,
  selectMinimumPrice,
  selectSorting,
  selectMapCoordinatesQueryString,
  selectLivingAreas,
  selectGarages,
  selectStories,
  selectPrimaryBedroom,
  selectRvGarage,
  selectMarketingStatus,
  selectReviews,
  activeAdultCommunitiesSelector,
  selectHomeStatus,
  selectMoveInDateRange,
  selectConsumerPromos,
  selectPriceReduction,
  selectHomeOwnersAssociationFee,
  selectFocusedSearchBoundsForUrl,
  selectLocationType,
  selectFocusedSearchActive,
  (
    bedrooms,
    bathrooms,
    natureAreas,
    green,
    pool,
    gated,
    golfcourse,
    sports,
    parks,
    views,
    waterFront,
    luxury,
    maximumPrice,
    minimumPrice,
    sorting,
    mapCoordinates,
    livingAreas,
    garages,
    stories,
    primaryBedroom,
    rvGarage,
    marketingStatus,
    reviews,
    activeAdult,
    homeStatus,
    moveInDateRange,
    consumerPromos,
    priceReduction,
    hasHomeOwnersAssociationFee,
    focusedSearchBounds,
    locationType,
    focusedSearchActive,
  ) => {

    const parameters = new URLSearchParams();

    // Helper function to append parameters only if they are valid
    const appendIfValid = (key, value) => {
      if (value && value !== "0") {
        parameters.append(key, value);
      }
    };

    // Transforms the primary bed store information into a recognizable query string.
    const getPrimaryBed = (primaryBed) => {
      switch (primaryBed) {
        case "1":
          return "up";
        case "2":
          return "down";
        default:
          return null;
      }
    };

    // Transform the move in date information into a recognizable query string.
    const getAvailability = (status, moveInDate) => {
      if (moveInDate) {
        return moveInDate;
      }

      return !hasInventoryStatusCombination(status) && hasAvailableNow(status) ? "now" : null;
    };

    appendIfValid('bed', bedrooms);
    appendIfValid('bath', bathrooms);
    appendIfValid('natureareas', natureAreas);
    appendIfValid('green', green);
    appendIfValid('pool', pool);
    appendIfValid('gated', gated);
    appendIfValid('golfcourse', golfcourse);
    appendIfValid('sports', sports);
    appendIfValid('parks', parks);
    appendIfValid('views', views);
    appendIfValid('waterfront', waterFront);
    appendIfValid('luxury', luxury);
    appendIfValid('pricehigh', maximumPrice);
    appendIfValid('pricelow', minimumPrice);
    appendIfValid('living', livingAreas);
    appendIfValid('garage', garages);
    appendIfValid('stories', stories);
    appendIfValid('rvgarage', rvGarage);
    appendIfValid('reviews', reviews);
    appendIfValid('hotdeals', consumerPromos);
    appendIfValid('pricedrop', priceReduction);
    appendIfValid('hoa', hasHomeOwnersAssociationFee);
    appendIfValid('comingsoon', isComingSoon(marketingStatus));
    appendIfValid('closeout', isCloseOut(marketingStatus));
    appendIfValid('grandopening', isGrandOpening(marketingStatus));
    appendIfValid('active', isActive(marketingStatus));
    appendIfValid('primarybed', getPrimaryBed(primaryBedroom));
    appendIfValid('availability', getAvailability(homeStatus, moveInDateRange));
    appendIfValid('inventory', hasInventoryStatusCombination(homeStatus));

    const { exclude, only } = activeAdult;
    appendIfValid('adult', only);
    appendIfValid('exclude55', exclude);

    // Facets are the first thing to appear in the query string, they must be alphabetically sorted.
    parameters.sort();

    // Map coordinates should appear after any user applied filter in the query string.
    const appendMapCoordinates = (coordinates) => {
      if (coordinates) {
        const [key, value] = coordinates.split('=');
        parameters.append(key, value);
      }
    };

    // Last thing to appear in the query string is any user applied sorting value
    const appendSortingOptions = (sort) => {
      if (sort && sort.sortBy && sort.sortBy !== SortType.CommunityId.toString()) {
        const sortingOption = SortType.fromKey(sort.sortBy.toLowerCase());
        parameters.append('sortby', sortingOption.getQueryStringValue(!sort.sortOrder));
      }
    };

    const constructQueryString = () => {
      const queryString = parameters.toString();
      return queryString ? `?${decodeURIComponent(queryString)}` : '';
    };

    appendMapCoordinates(mapCoordinates);

    // On STATE, min/max lat/lng are also written by viewportAutoSearch on every pan
    // (any zoom), not just by focusedSearch � only reflect these values in the query string while
    // focusedSearch is actually active, so a plain viewport-auto-search pan doesn't add them.
    const isState = locationType === LocationType.State.toString();

    if (focusedSearchBounds && !(isState && !focusedSearchActive)) {
      parameters.append('minlat', focusedSearchBounds.south);
      parameters.append('maxlat', focusedSearchBounds.north);
      parameters.append('minlng', focusedSearchBounds.west);
      parameters.append('maxlng', focusedSearchBounds.east);
    }

    appendSortingOptions(sorting);

    return constructQueryString();
  }
);

/**
 * This selector is meant for derive the Search Results Page no results query string based in the current store values.
 * The selector transforms the store data into a string that represents the filters applied by the user.
 */
export const selectConstructMinimumRequiredFiltersQueryString = createSelector(
  selectMapCoordinatesQueryString,
  selectSorting,
  selectResultType,
  (
    mapCoordinates,
    sorting,
    resultType,
  ) => {

    const parameters = new URLSearchParams();

    const appendMapCoordinates = (coordinates) => {
      if (coordinates) {
        const [key, value] = coordinates.split('=');
        parameters.append(key, value);
      }
    };

    const appendSortingOptions = (sort) => {
      if (sort && sort.sortBy && sort.sortBy !== SortType.CommunityId.toString()) {
        const sortingOption = SortType.fromKey(sort.sortBy.toLowerCase());
        parameters.append('sortby', sortingOption.getQueryStringValue(!sort.sortOrder));
      }
    };

    const constructQueryString = () => {
      const queryString = parameters.toString();
      return queryString ? `?${decodeURIComponent(queryString)}` : '';
    };

    appendMapCoordinates(mapCoordinates);

    if (resultType !== ResultType.Builders.toString()) {
      appendSortingOptions(sorting);
    }

    return constructQueryString();
  }
);

/**
 * Selects information about listing counts and query string constructor.
 */
export const selectCountsAndQueryString = createSelector(
  selectListingCount,
  selectFetchResultListingsStatus,
  selectResultType,
  selectConstructQueryString,
  selectConstructMinimumRequiredFiltersQueryString,
  (
    listingCounts,
    resultCountsStatus,
    resultType,
    queryString,
    minimumRequiredFiltersQueryString,
  ) =>
    ({ 
      listingCounts,
      resultCountsStatus,
      resultType,
      queryString,
      minimumRequiredFiltersQueryString
    })
  );

/**
 * This selector is created to retieve all the required information for tha native ads implementation
 * Returnig an Object with:
 * - Result Listing Status
 * - Presentation Type
 * - Paging
 * - Result Type
 * - Location
 * - Ads
 */
export const selectNativeAdInformation = createSelector(
  selectFetchResultListingsStatus,
  selectResultsPresentationType,
  selectPaging,
  selectResultType,
  selectLocation,
  (
      fetchResultListingsStatus,
      resultsPresentationType,
      paging,
      resultType,
      location,
  ) => ({ 
      fetchResultListingsStatus,
      resultsPresentationType,
      paging,
      resultType,
      location,
    })
  );

export const selectAutoSelectTabContext = createSelector(
  selectProductType,
  selectResultType,
  selectSearchParameters,
  selectAppliedFacets,
  selectLocation,
  (productType, resultType, searchParameters, appliedFilters, location) => ({
    counts: searchParameters?.counts ?? {},
    hasAppliedFilters: (appliedFilters ?? []).length > 0,
    location,
    productResultType: getProductResultTypeToRedirect(productType, resultType),
  })
);

export const selectHasAnyResultTypeTabResults = createSelector(
  selectListingCount,
  selectHomesCount,
  (listingCount = 0, homesCount = 0) => listingCount > 0 || homesCount > 0
);
